/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useRef, useState} from 'react';
import {Controller} from 'react-hook-form';
import {useIntl} from 'react-intl';
import PropTypes from 'prop-types';

import {AlertIcon, VisibilityIcon, VisibilityOffIcon} from '@salesforce/retail-react-app/app/components/icons';
import {useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';
import {
  Box, Checkbox, FormControl, FormErrorMessage, FormLabel, IconButton, Input, InputGroup,
  InputRightElement, Select, Text, Textarea,
} from '@salesforce/retail-react-app/app/components/shared/ui';
import {
  AlertErrorIcon,
  CheckoutVisibilityIcon,
  CheckoutVisibilityOffIcon,
  RemoveCircleIcon,
  SelectChevronIcon,
  UploadIcon,
} from '@amblique/king-living/app/components/icons';
import {FieldValidationStatus} from '@amblique/king-living/app/components/field/field-validation-status';
import {
  FieldLabelContent,
  isFieldRequired,
  isFloatingLabelVisible,
  isLabelDerivedPlaceholder,
  resolveFloatingFieldPlaceholder,
} from '@amblique/king-living/app/components/field/field-label-content';

const Field = ({
  name,
  label,
  formLabel,
  type = 'text',
  options = [],
  rules = {},
  error,
  placeholder,
  inputProps,
  control,
  autoComplete,
  defaultValue,
  helpText,
  children,
  inputRef,
  isDisabled,
  showClearButton = false,
  /** When true with type password, masked value shows * (native password type uses bullets). */
  passwordAsteriskMask = false,
  variant,
}) => {
  const intl = useIntl();
  const [hidePassword, setHidePassword] = useState(true);
  const [isFocused, setIsFocused] = useState(false);
  const isCheckoutPayment = variant === 'checkoutPayment';
  const PasswordIcon = isCheckoutPayment ?
    (hidePassword ? CheckoutVisibilityOffIcon : CheckoutVisibilityIcon) :
    (hidePassword ? VisibilityIcon : VisibilityOffIcon);
  const passwordIconLabel = hidePassword ?
    intl.formatMessage({
      id: 'field.password.assistive_msg.show_password',
      defaultMessage: 'Show password',
    }) :
    intl.formatMessage({
      id: 'field.password.assistive_msg.hide_password',
      defaultMessage: 'Hide password',
    });
  const styles = useMultiStyleConfig('Field', {variant});
  const isRequired = isFieldRequired(rules);
  const validationKeyDownRef = useRef(() => {});

  return (
    <Controller
      control={control}
      defaultValue={defaultValue}
      name={name}
      rules={rules}
      render={({field: {onBlur: fieldOnBlur, onChange, value, ref}, fieldState}) => {
        const _inputProps =
          typeof inputProps === 'function' ? inputProps({value, onChange}) : inputProps;

        const resolvedInputType =
          type === 'password' && hidePassword && !passwordAsteriskMask ? 'password' :
            type === 'password' && hidePassword && passwordAsteriskMask ? 'text' :
              type === 'password' && !hidePassword ? 'text' :
                type;

        const isAsteriskMasked = type === 'password' && passwordAsteriskMask && hidePassword;
        const inputDisplayValue = isAsteriskMasked ? '*'.repeat((value || '').length) : value;

        const handleInputChange = (e) => {
          const v = e.target.value;
          if (isAsteriskMasked) {
            if (v.length === 0) {
              onChange(e);

              return;
            }
            if (!/^\*+$/.test(v)) {
              onChange(e);
            }
          } else if (_inputProps?.onChange) {
            _inputProps.onChange(e);
          } else {
            onChange(e);
          }
        };

        const handlePasswordMaskedKeyDown = (e) => {
          _inputProps?.onKeyDown?.(e);
          if (!isAsteriskMasked) {
            validationKeyDownRef.current();

            return;
          }
          if (e.key === 'Backspace') {
            e.preventDefault();
            onChange({target: {value: (value || '').slice(0, -1)}});
            validationKeyDownRef.current();

            return;
          }
          if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
            e.preventDefault();
            onChange({target: {value: (value || '') + e.key}});
            validationKeyDownRef.current();
          }
        };

        const handlePasswordMaskedPaste = (e) => {
          if (!isAsteriskMasked) return;
          e.preventDefault();
          const text = e.clipboardData.getData('text');
          if (text) {
            onChange({target: {value: (value || '') + text}});
          }
        };

        const handleFocus = (e) => {
          setIsFocused(true);
          _inputProps?.onFocus?.(e);
        };

        const handleBlur = (e) => {
          setIsFocused(false);
          fieldOnBlur?.(e);
          _inputProps?.onBlur?.(e);
        };

        const isDisabledField = isDisabled || _inputProps?.isDisabled;
        const hasValue = value != null && value.toString().trim() !== '';
        // A select always paints its selected option, so it cannot hide the placeholder the way an
        // input can. Its label therefore floats on selection rather than on focus, which keeps the
        // resting state identical to the text fields and still shows the two one at a time.
        const isLabelVisible = type === 'select' ? hasValue : isFloatingLabelVisible({
          isFocused,
          value,
          isDisabled: isDisabledField,
        });
        const fieldPlaceholder = resolveFloatingFieldPlaceholder({
          placeholder,
          label,
          isRequired,
          isLabelVisible,
          isCheckoutPayment,
        });
        // Marks a placeholder that merely repeats the label, so the theme can hide it once the
        // floating label is up. Hint placeholders are left visible.
        const labelPlaceholderFlag =
          !isCheckoutPayment && isLabelDerivedPlaceholder({placeholder, label}) ? 'true' : undefined;
        const ariaDescribedBy = error ? `${name}-error` : undefined;

        return (
          <FormControl id={name} isInvalid={error}>
            <InputGroup className="field__input-group" {...styles.inputGroup}>
              {['text', 'password', 'email', 'phone', 'tel', 'number'].includes(
                type
              ) && (
                <Input
                  ref={(node) => {
                    ref(node);
                    if (inputRef) inputRef.current = node;
                  }}
                  id={name}
                  aria-describedby={ariaDescribedBy}
                  aria-invalid={error ? 'true' : undefined}
                  aria-required={isRequired ? 'true' : undefined}
                  autoComplete={autoComplete}
                  data-label-placeholder={labelPlaceholderFlag}
                  placeholder={fieldPlaceholder}
                  pr={
                    showClearButton && isFocused && value ?
                      12 :
                      _inputProps?.pr
                  }
                  type={resolvedInputType}
                  value={inputDisplayValue}
                  {..._inputProps}
                  {...styles.input}
                  onBlur={handleBlur}
                  onChange={handleInputChange}
                  onFocus={handleFocus}
                  onKeyDown={handlePasswordMaskedKeyDown}
                  onPaste={handlePasswordMaskedPaste}
                />
              )}

              {showClearButton && isFocused && value && !isDisabled && type !== 'password' && (
                <InputRightElement {...styles.clearButtonContainer}>
                  <IconButton
                    aria-label={intl.formatMessage({
                      id: 'field.clear.assistive_msg',
                      defaultMessage: 'Clear field',
                    })}
                    icon={<RemoveCircleIcon {...styles.clearIcon} />}
                    variant="ghost"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      onChange('');
                    }}
                    {...styles.clearButton}
                  />
                </InputRightElement>
              )}

              {type === 'hidden' && (
                <input
                  ref={ref}
                  type="hidden"
                  value={value}
                  onChange={onChange}
                  {..._inputProps}
                />
              )}

              {type === 'password' && (
                <InputRightElement className="field__password-icon-container" {...styles.passwordIconButtonContainer}>
                  <IconButton
                    aria-label={passwordIconLabel}
                    icon={<PasswordIcon {...styles.passwordIcon} />}
                    variant="ghosted"
                    onClick={() => setHidePassword(!hidePassword)}
                    {...styles.passwordIconButton}
                  />
                </InputRightElement>
              )}

              {type === 'select' && (
                <Box className="select-container" position="relative" width="100%">
                  <Select
                    ref={ref}
                    id={name}
                    aria-describedby={ariaDescribedBy}
                    aria-required={isRequired ? 'true' : undefined}
                    data-placeholder-shown={hasValue ? undefined : 'true'}
                    icon={<SelectChevronIcon boxSize="20px" />}
                    iconColor="brandColors.primaryDarkLinen"
                    isDisabled={isDisabled}
                    placeholder={fieldPlaceholder}
                    value={value}
                    onBlur={(event) => {
                      setIsFocused(false);
                      fieldOnBlur?.(event);
                      _inputProps?.onBlur?.(event);
                    }}
                    onChange={onChange}
                    onFocus={(event) => {
                      setIsFocused(true);
                      _inputProps?.onFocus?.(event);
                    }}
                    {..._inputProps}
                    {...styles.select}
                    sx={{
                      pt: 6.875,
                      pb: 2.375,
                      ...styles.select?.sx,
                    }}
                  >
                    {options.map((opt) => (
                      <option key={`${opt.label}-${opt.value}`} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Select>
                  {label && (
                    <FormLabel
                      htmlFor={name}
                      {...styles.label}
                      {...styles.labelSelect}
                      sx={{
                        ...styles.label?.sx,
                        position: 'absolute',
                        opacity: isLabelVisible ? 1 : 0,
                        top: isLabelVisible ? 2 : 4.5,
                        left: 4.5,
                        color: error ? 'brandColors.error' : 'brandColors.primaryDarkLinen',
                        fontSize: '12px',
                        fontWeight: 'normal',
                        lineHeight: '120%',
                        pointerEvents: 'none',
                        zIndex: 1,
                      }}
                    >
                      <FieldLabelContent label={label} rules={rules} />
                    </FormLabel>
                  )}
                </Box>
              )}

              {type === 'textarea' && (
                <>
                  <Textarea
                    ref={(node) => {
                      ref(node);
                      if (inputRef) inputRef.current = node;
                    }}
                    id={name}
                    aria-describedby={ariaDescribedBy}
                    aria-invalid={error ? 'true' : undefined}
                    aria-required={isRequired ? 'true' : undefined}
                    data-label-placeholder={labelPlaceholderFlag}
                    placeholder={fieldPlaceholder}
                    value={value}
                    onBlur={handleBlur}
                    onChange={onChange}
                    onFocus={handleFocus}
                    onKeyDown={(e) => {
                      _inputProps?.onKeyDown?.(e);
                      validationKeyDownRef.current();
                    }}
                    {..._inputProps}
                    {...styles.textarea}
                  />
                  {label && (
                    <FormLabel
                      htmlFor={name}
                      {...styles.label}
                      {...(isCheckoutPayment ? undefined : {
                        sx: {
                          ...styles.label.sx,
                          opacity: isFocused || (value && value.toString().trim() !== '') || isDisabledField ? 1 : 0,
                          top: isFocused || (value && value.toString().trim() !== '') || isDisabledField ? 2 : 4.5,
                          color: error ? 'brandColors.error' : isDisabledField ? 'brandColors.primaryDarkLinen' : isFocused || (value && value.toString().trim() !== '') ? 'brandColors.primaryDarkLinen' : 'brandColors.greyscale80',
                        },
                      })}
                    >
                      <FieldLabelContent label={label} rules={rules} />
                    </FormLabel>
                  )}
                </>
              )}

              {type === 'checkbox' && (
                <Checkbox
                  ref={ref}
                  id={name}
                  isChecked={value}
                  onChange={(e) => onChange(e.target.checked)}
                  {..._inputProps}
                  {...styles.checkbox}
                >
                  {formLabel || label}
                </Checkbox>
              )}

              {type === 'file' && (
                <Box display="flex" flexDirection="column" position="relative" width="100%">
                  <Box
                    as="label"
                    htmlFor={name}
                    {...styles.fileInputContainer}
                    sx={error ? {
                      borderWidth: '2px', // Keep 2px to maintain consistent size
                      borderColor: 'brandColors.greyscale10',
                      backgroundColor: 'white',
                      px: 0,
                    } : {}}
                  >
                    <input
                      ref={ref}
                      id={name}
                      type="file"
                      onChange={(e) => {
                        const file = e.target.files?.[0];

                        onChange(file);
                      }}
                      {..._inputProps}
                      style={{
                        position: 'absolute',
                        inset: 0,
                        opacity: 0,
                        width: '100%',
                        height: '100%',
                        cursor: 'pointer',
                        zIndex: 1,
                      }}
                    />
                    <UploadIcon {...styles.fileInputIcon} />
                    <Text {...styles.fileInputPrimaryText}>
                      Upload a file or drag and drop
                    </Text>
                    <Text {...styles.fileInputSecondaryText}>
                      Formats accepted: JPG, PNG or PDF
                      <br />
                      File size must not exceed 20MB
                    </Text>
                  </Box>
                  {error && (
                    <Box {...styles.fileInputErrorAlert}>
                      <AlertErrorIcon aria-hidden="true" {...styles.fileInputErrorAlertIcon} />
                      <Text>{error.message}</Text>
                    </Box>
                  )}
                </Box>
              )}

              {!['checkbox', 'radio', 'hidden', 'file', 'textarea', 'select'].includes(type) &&
                (formLabel || (
                  <FormLabel
                    htmlFor={name}
                    {...styles.label}
                    {...(isCheckoutPayment ? undefined : {
                      sx: {
                        ...styles.label.sx,
                        opacity: isFocused || (value && value.toString().trim() !== '') || isDisabledField ? 1 : 0,
                        top: isFocused || (value && value.toString().trim() !== '') || isDisabledField ? 2 : 4.5,
                        color: error ? 'brandColors.error' : isDisabledField ? 'brandColors.primaryDarkLinen' : isFocused || (value && value.toString().trim() !== '') ? 'brandColors.primaryDarkLinen' : 'brandColors.greyscale80',
                      },
                    })}
                  >
                    <FieldLabelContent label={label} rules={rules} />
                  </FormLabel>
                ))}

              {children}
            </InputGroup>

            {type !== 'hidden' && type !== 'file' && (
              <FormErrorMessage
                aria-atomic="true"
                aria-live="assertive"
                id={`${name}-error`}
                minH="1px"
                role="alert"
                visibility={error ? 'visible' : 'hidden'}
                {...styles.formErrorMessage}
              >
                {error && (
                  <>
                    <AlertIcon aria-hidden="true" {...styles.formErrorMessageIcon} />
                    {error.message}
                  </>
                )}
              </FormErrorMessage>
            )}

            {helpText}

            <FieldValidationStatus
              enabled={!['checkbox', 'hidden', 'file'].includes(type)}
              errorMessage={error?.message}
              formatMessage={intl.formatMessage}
              id={`${name}-status`}
              invalid={!!error}
              keyDownRef={validationKeyDownRef}
              label={typeof (label || formLabel) === 'string' ? (label || formLabel) : undefined}
              value={value}
            />
          </FormControl>
        );
      }}
    />
  );
};

Field.propTypes = {
  name: PropTypes.string,
  label: PropTypes.string,
  autoComplete: PropTypes.string,
  formLabel: PropTypes.any,
  type: PropTypes.oneOf([
    'text',
    'number',
    'password',
    'email',
    'phone',
    'tel',
    'select',
    'checkbox',
    'hidden',
    'file',
    'textarea'
  ]),
  options: PropTypes.arrayOf(PropTypes.shape({label: PropTypes.string, value: PropTypes.any})),
  rules: PropTypes.object,
  error: PropTypes.shape({message: PropTypes.string}),
  placeholder: PropTypes.string,
  inputProps: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  control: PropTypes.object,
  defaultValue: PropTypes.any,
  helpText: PropTypes.any,
  children: PropTypes.any,
  inputRef: PropTypes.object,
  isDisabled: PropTypes.bool,
  showClearButton: PropTypes.bool,
  passwordAsteriskMask: PropTypes.bool,
  variant: PropTypes.string,
};

export default Field;
